import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { RichTextElementDataType } from '@/components/sections/builder/elements/body/rich-text/types';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Separator } from '@/components/ui/separator';

export default function ElementSettingTeks({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as RichTextElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  const CONTENT_POSITION_OPTIONS = [
    { label: 'Top Left', value: 'top-left' },
    { label: 'Top Center', value: 'top-center' },
    { label: 'Top Right', value: 'top-right' },
    { label: 'Middle Left', value: 'middle-left' },
    { label: 'Middle Center', value: 'middle-center' },
    { label: 'Middle Right', value: 'middle-right' },
    { label: 'Bottom Left', value: 'bottom-left' },
    { label: 'Bottom Center', value: 'bottom-center' },
    { label: 'Bottom Right', value: 'bottom-right' },
  ];

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Dekstop Settings</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Desktop content position</h4>
        <InputSelect
          defaultValue={data.contentPosition}
          options={CONTENT_POSITION_OPTIONS.map((obj) => ({
            label: obj.label,
            value: obj.value,
          }))}
          onChange={(v) => builder.setElementData(element.id, { contentPosition: v })}
        />
      </div>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Desktop content alignment</h4>
        <InputSelect
          defaultValue={data.contentAlign}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Mobile Settings</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Mobile content alignment</h4>
        <InputSelect
          defaultValue={data.contentAlignMobile}
          options={[
            { label: 'Left', value: 'left' },
            { label: 'Center', value: 'center' },
            { label: 'Right', value: 'right' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { contentAlignMobile: v })}
        />
      </div>

      <Separator className='my-4' />
      <h1 className='mb-4 text-sm font-bold'>Style</h1>
      <div className='mb-2 text-xs'>Color scheme</div>
      <SchemeSetting
        value={data.containerColorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            containerColorScheme: theme,
          });
        }}
      />
      <div className='mt-1 text-xs'>
        To edit all your theme&apos;s colors, go to your color theme settings.
      </div>

      <div className='mb-2 mt-4'>Overlay</div>
      <ColorPicker
        color={data.overlay}
        inputColor={data.overlay}
        onChangeColorComplete={(color) => onChangeColor(color)}
        onChangeInputColor={(e) => builder.setElementData(element.id, { overlay: e.target.value })}
        onChangePercentage={(percent) => {
          builder.setElementData(element.id, {
            overlayOpacity: Number(percent),
          });
        }}
      />
    </div>
  );
}
